import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Modal,
    Row,
    Col,
    Typography,
    Table,
    Button,
    Space,
    Tag,
    App,
    DatePicker,
    Empty,
    Select,
    Spin,
    Statistic,
    Card,
    Alert
} from 'antd';
import {
    ReloadOutlined,
    PrinterOutlined,
    BarChartOutlined,
    FileTextOutlined,
    UserOutlined,
    ClockCircleOutlined,
    DownloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title as ChartTitle,
    Tooltip as ChartTooltip,
    Legend
} from 'chart.js';
import { admin } from '../services/api';
import { chartToBase64 } from '../relatorios/utils/chartToImage';

const { Text } = Typography;
const { RangePicker } = DatePicker;

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, ChartTooltip, Legend);

const currentYear = dayjs().year();
const dashboardYearOptions = Array.from({ length: 5 }, (_, index) => currentYear - index);

const formatDashboardDate = (value) => {
    if (!value) return '-';
    return dayjs(value).format('DD/MM/YYYY HH:mm');
};

const formatDashboardDuration = (value) => {
    if (!value) return 'N/A';
    return value;
};

const formatDashboardNumber = (value) => Number(value || 0).toLocaleString('pt-BR');

const ImpressoraDashboardModal = ({
    visible,
    onCancel,
    impressora = null,
    printerName = '',
    printerItems = []
}) => {
    const { message } = App.useApp();

    const [fetching, setFetching] = useState(false);
    const [currentImpressora, setCurrentImpressora] = useState(null);

    const [dashboardLoading, setDashboardLoading] = useState(false);
    const [dashboardError, setDashboardError] = useState('');
    const [dashboardData, setDashboardData] = useState(null);
    const [dashboardYear, setDashboardYear] = useState(currentYear);
    const [dashboardPeriod, setDashboardPeriod] = useState(null);
    const [exportingPdf, setExportingPdf] = useState(false);
    const [branchesList, setBranchesList] = useState([]);

    const dashboardCacheRef = useRef(new Map());

    // Carregar filiais cadastradas para resolução de filial/unidade
    useEffect(() => {
        let isMounted = true;
        const fetchBranches = async () => {
            try {
                const response = await admin.getBranches();
                if (isMounted && Array.isArray(response)) {
                    setBranchesList(response);
                }
            } catch (err) {
                console.warn('Não foi possível carregar lista de filiais:', err?.message);
            }
        };
        fetchBranches();
        return () => { isMounted = false; };
    }, []);

    // Obter os dados da impressora se apenas o nome for passado
    useEffect(() => {
        if (visible) {
            const initModal = async () => {
                let imp = impressora;

                if (!imp && printerName) {
                    setFetching(true);
                    try {
                        const response = await admin.getImpressoraByName(printerName);
                        if (response && response.success && response.data) {
                            imp = response.data;
                        }
                    } catch (error) {
                        console.warn('Impressora não cadastrada no backend:', error);
                    } finally {
                        setFetching(false);
                    }

                    // Se não encontrou no backend, inicializa objeto de impressora com o nome fornecido
                    if (!imp) {
                        imp = {
                            id: printerName,
                            nome: printerName,
                            marca: 'Genérica',
                            marca_nome: 'Genérica',
                            modelo: 'Genérico',
                            modelo_nome: 'Genérico',
                            emulador: 'ZPL'
                        };
                    }
                }

                setCurrentImpressora(imp);

                // Reseta os filtros
                setDashboardYear(currentYear);
                setDashboardPeriod(null);
                setDashboardData(null);
                setDashboardError('');
            };

            initModal();
        } else {
            setCurrentImpressora(null);
            setDashboardData(null);
        }
    }, [visible, impressora, printerName]);

    const getDashboardCacheKey = useCallback((printerId, filters) => JSON.stringify({
        printerId,
        year: filters.year || null,
        startDate: filters.startDate || null,
        endDate: filters.endDate || null
    }), []);

    const dashboardFilters = useMemo(() => ({
        year: dashboardYear,
        startDate: dashboardPeriod?.[0] ? dashboardPeriod[0].format('YYYY-MM-DD') : undefined,
        endDate: dashboardPeriod?.[1] ? dashboardPeriod[1].format('YYYY-MM-DD') : undefined
    }), [dashboardYear, dashboardPeriod]);

    // Constrói os dados do dashboard localmente a partir dos itens em memória
    const buildLocalDashboardData = useCallback((items, filters) => {
        const rawList = Array.isArray(items) ? items : [];

        // Filtra por ano e período se especificado
        const filtered = rawList.filter(item => {
            const dateStr = item.DATA || item.data || item.createdAt;
            if (!dateStr) return true;
            const d = dayjs(dateStr);
            if (!d.isValid()) return true;

            if (filters.startDate && d.isBefore(dayjs(filters.startDate), 'day')) return false;
            if (filters.endDate && d.isAfter(dayjs(filters.endDate), 'day')) return false;
            if (filters.year && d.year() !== Number(filters.year)) return false;

            return true;
        });

        let totalPagesPrinted = 0;
        let totalReprints = 0;
        const usersMap = new Map();
        const companiesMap = new Map();
        const docTypesMap = new Map();
        const monthlyMap = new Map();
        const docsSet = new Set();
        let firstDate = null;
        let lastDate = null;

        filtered.forEach(item => {
            const volumes = Number(item.VOLUMES || item.volumes || 1);
            const isImp = item.STATUS_ETIQUETA === 1 || item.status === 1;
            const pages = isImp ? volumes : 1;
            totalPagesPrinted += pages;

            // Usuário / Veículo
            const user = item.CARRO || item.carro || item.usuario || item.USUARIO || 'Não informado';
            usersMap.set(user, (usersMap.get(user) || 0) + pages);

            // Filial / Unidade
            const rawFilial = item.FILIAL ?? item.filial ?? item.empresa ?? item.EMPRESA ?? item.unidade ?? item.sigla ?? item.branchId;
            let branchCode = (rawFilial != null && String(rawFilial).trim() !== '') ? String(rawFilial).trim() : '-';
            let branchName = item.unidade || item.sigla || item.nome_filial || item.branchName || '';

            if (branchesList && branchesList.length > 0 && rawFilial != null) {
                const foundBranch = branchesList.find(b =>
                    String(b.codigo).trim() === branchCode ||
                    String(b.sigla).trim().toUpperCase() === branchCode.toUpperCase() ||
                    String(b.id).trim() === branchCode ||
                    String(b.name).trim().toLowerCase() === branchCode.toLowerCase()
                );
                if (foundBranch) {
                    branchCode = foundBranch.codigo || foundBranch.sigla || branchCode;
                    branchName = foundBranch.name
                        ? (foundBranch.sigla ? `${foundBranch.name} (${foundBranch.sigla})` : foundBranch.name)
                        : (foundBranch.sigla || `Filial ${branchCode}`);
                }
            }

            if (!branchName) {
                branchName = branchCode !== '-' ? `Filial ${branchCode}` : 'Filial / Unidade não informada';
            }

            const compKey = `${branchCode}_${branchName}`;
            if (!companiesMap.has(compKey)) {
                companiesMap.set(compKey, {
                    companyName: branchName,
                    companyCode: branchCode,
                    totalPages: 0,
                    totalReprints: 0
                });
            }
            companiesMap.get(compKey).totalPages += pages;

            // Tipo de Documento
            const docType = item.natureza || (item.Nota ? 'NF-e' : 'Etiqueta');
            docTypesMap.set(docType, (docTypesMap.get(docType) || 0) + pages);

            // Documentos únicos
            const docKey = `${item.Nota || item.nota || ''}_${item.SERIE || item.serie || ''}_${item.chave || ''}`;
            docsSet.add(docKey);

            // Datas
            const dateStr = item.DATA || item.data || item.createdAt;
            if (dateStr) {
                const d = dayjs(dateStr);
                if (d.isValid()) {
                    if (!firstDate || d.isBefore(firstDate)) firstDate = d;
                    if (!lastDate || d.isAfter(lastDate)) lastDate = d;

                    const mKey = d.format('YYYY-MM');
                    const mLabel = d.format('MM/YYYY');
                    if (!monthlyMap.has(mKey)) {
                        monthlyMap.set(mKey, { label: mLabel, totalPages: 0, totalReprints: 0 });
                    }
                    monthlyMap.get(mKey).totalPages += pages;
                }
            }
        });

        if (monthlyMap.size === 0) {
            const currentMKey = dayjs().format('YYYY-MM');
            monthlyMap.set(currentMKey, {
                label: dayjs().format('MM/YYYY'),
                totalPages: totalPagesPrinted,
                totalReprints: 0
            });
        }

        const monthlyVolume = Array.from(monthlyMap.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([, val]) => val);

        const topUsers = Array.from(usersMap.entries())
            .map(([user, totalPages]) => ({ user, totalPages, totalReprints: 0 }))
            .sort((a, b) => b.totalPages - a.totalPages);

        const companiesUsed = Array.from(companiesMap.values())
            .sort((a, b) => b.totalPages - a.totalPages);

        const documentTypes = Array.from(docTypesMap.entries())
            .map(([type, totalPages]) => ({ type, totalPages, totalReprints: 0 }))
            .sort((a, b) => b.totalPages - a.totalPages);

        const estimatedSecs = totalPagesPrinted * 2;
        const hours = Math.floor(estimatedSecs / 3600);
        const mins = Math.floor((estimatedSecs % 3600) / 60);
        const secs = estimatedSecs % 60;
        const timeLabel = hours > 0 ? `${hours}h ${mins}m ${secs}s` : mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;

        return {
            printer: {
                nome: currentImpressora?.nome || printerName,
                marca: currentImpressora?.marca_nome || currentImpressora?.marca || 'Genérica',
                modelo: currentImpressora?.modelo_nome || currentImpressora?.modelo || 'Genérico',
                emulador: currentImpressora?.emulador || 'ZPL'
            },
            metrics: {
                totalPagesPrinted,
                totalDocuments: docsSet.size || filtered.length,
                totalUsers: usersMap.size,
                estimatedTotalPrintTimeLabel: timeLabel,
                totalReprints,
                totalCompaniesUsed: companiesMap.size,
                firstPrintAt: firstDate ? firstDate.toISOString() : null,
                lastPrintAt: lastDate ? lastDate.toISOString() : null
            },
            monthlyVolume,
            companiesUsed: { items: companiesUsed },
            topUsers: { items: topUsers },
            documentTypes: { items: documentTypes }
        };
    }, [currentImpressora, printerName, branchesList]);

    const loadDashboard = useCallback(async ({ forceRefresh = false } = {}) => {
        if (!currentImpressora?.id) return;

        const cacheKey = getDashboardCacheKey(currentImpressora.id, dashboardFilters);
        if (!forceRefresh) {
            const cachedEntry = dashboardCacheRef.current.get(cacheKey);
            if (cachedEntry && (Date.now() - cachedEntry.timestamp) < 5 * 60 * 1000) {
                setDashboardData(cachedEntry.data);
                setDashboardError('');
                return;
            }
        }

        setDashboardLoading(true);
        setDashboardError('');

        try {
            let response = null;
            if (!isNaN(Number(currentImpressora.id))) {
                try {
                    response = await admin.getImpressoraDashboard(currentImpressora.id, dashboardFilters);
                } catch (apiErr) {
                    console.warn('API getImpressoraDashboard não disponível, usando dados locais:', apiErr?.message);
                }
            }

            if (response?.success && response.data) {
                dashboardCacheRef.current.set(cacheKey, {
                    timestamp: Date.now(),
                    data: response.data
                });
                setDashboardData(response.data);
            } else {
                const localData = buildLocalDashboardData(printerItems, dashboardFilters);
                dashboardCacheRef.current.set(cacheKey, {
                    timestamp: Date.now(),
                    data: localData
                });
                setDashboardData(localData);
            }
        } catch (error) {
            const localData = buildLocalDashboardData(printerItems, dashboardFilters);
            setDashboardData(localData);
        } finally {
            setDashboardLoading(false);
        }
    }, [dashboardFilters, getDashboardCacheKey, currentImpressora, printerItems, buildLocalDashboardData]);

    useEffect(() => {
        if (!visible || !currentImpressora?.id) {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            loadDashboard();
        }, 250);

        return () => window.clearTimeout(timeoutId);
    }, [visible, currentImpressora, dashboardFilters, loadDashboard]);

    // O código de exportação de PDF continua abaixo...
    const buildDashboardPrintHtml = useCallback(({ monthlyChartImage, companyChartImage, topUsersChartImage }) => {
        const localCompanyItems = dashboardData?.companiesUsed?.items || [];
        const localDocumentTypeItems = dashboardData?.documentTypes?.items || [];
        const localTopUsersItems = dashboardData?.topUsers?.items || [];
        const printerName = dashboardData?.printer?.nome || currentImpressora?.nome || 'Impressora';
        const companyTags = localCompanyItems.length > 0
            ? localCompanyItems.map((item) => `<span class="tag">${item.companyName}</span>`).join('')
            : '<span class="muted">Nenhuma filial/unidade encontrada.</span>';

        const documentTypeRows = localDocumentTypeItems.length > 0
            ? localDocumentTypeItems.map((item) => `
                <tr>
                    <td>${item.type}</td>
                    <td>${formatDashboardNumber(item.totalPages)}</td>
                    <td>${formatDashboardNumber(item.totalReprints)}</td>
                </tr>
            `).join('')
            : '<tr><td colspan="3">Nenhum tipo de documento encontrado.</td></tr>';

        const userRows = localTopUsersItems.length > 0
            ? localTopUsersItems.map((item) => `
                <tr>
                    <td>${item.user}</td>
                    <td>${formatDashboardNumber(item.totalPages)}</td>
                    <td>${formatDashboardNumber(item.totalReprints)}</td>
                </tr>
            `).join('')
            : '<tr><td colspan="3">Nenhum usuário encontrado.</td></tr>';

        const companyRows = localCompanyItems.length > 0
            ? localCompanyItems.map((item) => `
                <tr>
                    <td>${item.companyName}</td>
                    <td>${item.companyCode ?? '-'}</td>
                    <td>${formatDashboardNumber(item.totalPages)}</td>
                    <td>${formatDashboardNumber(item.totalReprints)}</td>
                </tr>
            `).join('')
            : '<tr><td colspan="4">Nenhuma filial/unidade encontrada.</td></tr>';

        return `
<!DOCTYPE html>
<html lang="pt-BR">
<head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Dashboard de Uso - ${printerName}</title>
    <style>
        @page { size: A4 portrait; margin: 12mm; }
        * { box-sizing: border-box; }
        body { font-family: Arial, sans-serif; color: #1f1f1f; margin: 0; }
        .container { width: 100%; }
        .header { display: flex; justify-content: space-between; gap: 16px; align-items: flex-start; margin-bottom: 16px; }
        .title { font-size: 22px; font-weight: 700; margin: 0 0 4px; }
        .subtitle { color: #595959; margin: 0; }
        .tags { display: flex; flex-wrap: wrap; gap: 8px; margin: 12px 0 0; }
        .tag { display: inline-block; background: #f0f5ff; color: #1d39c4; border: 1px solid #adc6ff; border-radius: 999px; padding: 4px 10px; font-size: 12px; }
        .muted { color: #8c8c8c; }
        .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 16px; }
        .card { border: 1px solid #f0f0f0; border-radius: 10px; padding: 12px; page-break-inside: avoid; }
        .label { color: #595959; font-size: 12px; margin-bottom: 6px; }
        .value { font-size: 22px; font-weight: 700; }
        .section { margin-bottom: 16px; page-break-inside: avoid; }
        .section-title { font-size: 16px; font-weight: 700; margin: 0 0 10px; }
        .chart { width: 100%; text-align: center; border: 1px solid #f0f0f0; border-radius: 10px; padding: 12px; }
        .chart img { max-width: 100%; height: auto; }
        .two-col { display: grid; grid-template-columns: 1.3fr 1fr; gap: 16px; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th, td { border: 1px solid #f0f0f0; padding: 8px; text-align: left; }
        th { background: #fafafa; }
        .summary-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <div>
                <h1 class="title">Dashboard de Uso - ${printerName}</h1>
                <p class="subtitle">Exportado em ${dayjs().format('DD/MM/YYYY HH:mm')}</p>
                <div class="tags">
                    <span class="tag">${dashboardData?.printer?.marca || currentImpressora?.marca_nome || 'Marca não informada'}</span>
                    <span class="tag">${dashboardData?.printer?.modelo || currentImpressora?.modelo_nome || 'Modelo não informado'}</span>
                    <span class="tag">${dashboardData?.printer?.emulador || currentImpressora?.emulador || 'Emulador não informado'}</span>
                </div>
            </div>
            <div>
                <div class="label">Filtro aplicado</div>
                <div>${dashboardPeriod?.[0] && dashboardPeriod?.[1]
                ? `${dashboardPeriod[0].format('DD/MM/YYYY')} até ${dashboardPeriod[1].format('DD/MM/YYYY')}`
                : `Ano ${dashboardYear}`}</div>
            </div>
        </div>

        <div class="grid">
            <div class="card"><div class="label">Total de páginas impressas</div><div class="value">${formatDashboardNumber(dashboardData?.metrics?.totalPagesPrinted)}</div></div>
            <div class="card"><div class="label">Tipos de documentos</div><div class="value">${formatDashboardNumber(dashboardData?.metrics?.totalDocuments)}</div></div>
            <div class="card"><div class="label">Usuários ativos</div><div class="value">${formatDashboardNumber(dashboardData?.metrics?.totalUsers)}</div></div>
            <div class="card"><div class="label">Tempo de impressão</div><div class="value">${formatDashboardDuration(dashboardData?.metrics?.estimatedTotalPrintTimeLabel)}</div></div>
            <div class="card"><div class="label">Reimpressões</div><div class="value">${formatDashboardNumber(dashboardData?.metrics?.totalReprints)}</div></div>
            <div class="card"><div class="label">Filiais / Unidades utilizadas</div><div class="value">${formatDashboardNumber(dashboardData?.metrics?.totalCompaniesUsed)}</div></div>
        </div>

        <div class="section two-col">
            <div class="chart">
                <h2 class="section-title">Volume de impressões por mês</h2>
                ${monthlyChartImage ? `<img src="${monthlyChartImage}" alt="Volume por mês" />` : '<p class="muted">Sem dados para o gráfico.</p>'}
            </div>
            <div class="chart">
                <h2 class="section-title">Filiais / Unidades e quantidade de registros</h2>
                ${companyChartImage ? `<img src="${companyChartImage}" alt="Filiais / Unidades e quantidade de registros" />` : '<p class="muted">Sem dados para o gráfico.</p>'}
            </div>
        </div>

        <div class="section two-col">
            <div class="chart">
                <h2 class="section-title">Usuários que mais utilizaram a impressora</h2>
                ${topUsersChartImage ? `<img src="${topUsersChartImage}" alt="Top usuários" />` : '<p class="muted">Sem dados para o gráfico.</p>'}
            </div>
            <div class="card">
                <h2 class="section-title">Resumo do período</h2>
                <div class="summary-row">
                    <div>
                        <div class="label">Primeira impressão</div>
                        <div>${formatDashboardDate(dashboardData?.metrics?.firstPrintAt)}</div>
                    </div>
                    <div>
                        <div class="label">Última impressão</div>
                        <div>${formatDashboardDate(dashboardData?.metrics?.lastPrintAt)}</div>
                    </div>
                </div>
                <div style="margin-top: 12px;">
                    <div class="label">Filiais / Unidades de uso</div>
                    <div class="tags">${companyTags}</div>
                </div>
            </div>
        </div>

        <div class="section">
            <h2 class="section-title">Ranking de usuários</h2>
            <table>
                <thead><tr><th>Usuário</th><th>Páginas</th><th>Reimpressões</th></tr></thead>
                <tbody>${userRows}</tbody>
            </table>
        </div>

        <div class="section">
            <h2 class="section-title">Ranking por tipo de documento</h2>
            <table>
                <thead><tr><th>Tipo de Documento</th><th>Páginas</th><th>Reimpressões</th></tr></thead>
                <tbody>${documentTypeRows}</tbody>
            </table>
        </div>

        <div class="section">
            <h2 class="section-title">Filiais / Unidades onde a impressora foi utilizada</h2>
            <table>
                <thead><tr><th>Filial / Unidade</th><th>Código</th><th>Páginas</th><th>Reimpressões</th></tr></thead>
                <tbody>${companyRows}</tbody>
            </table>
        </div>
    </div>
</body>
</html>
        `;
    }, [dashboardData, dashboardPeriod, dashboardYear, currentImpressora]);

    const handleExportDashboardPdf = useCallback(async () => {
        if (!dashboardData) {
            message.error('Carregue o dashboard antes de exportar o PDF.');
            return;
        }

        setExportingPdf(true);

        try {
            const localMonthlyVolumeData = dashboardData?.monthlyVolume || [];
            const localCompanyItems = dashboardData?.companiesUsed?.items || [];
            const localTopUsersItems = dashboardData?.topUsers?.items || [];

            const localMonthlyVolumeChartData = {
                labels: localMonthlyVolumeData.map((item) => item.label),
                datasets: [
                    {
                        label: 'Páginas impressas',
                        data: localMonthlyVolumeData.map((item) => item.totalPages),
                        backgroundColor: 'rgba(24, 144, 255, 0.7)',
                        borderRadius: 6
                    }
                ]
            };

            const localCompanyChartData = {
                labels: localCompanyItems.map((item) => item.companyName),
                datasets: [
                    {
                        label: 'Registros',
                        data: localCompanyItems.map((item) => item.totalPages),
                        backgroundColor: 'rgba(82, 196, 26, 0.75)',
                        borderRadius: 6
                    }
                ]
            };

            const localTopUsersChartData = {
                labels: localTopUsersItems.map((item) => item.user),
                datasets: [
                    {
                        label: 'Páginas impressas',
                        data: localTopUsersItems.map((item) => item.totalPages),
                        backgroundColor: 'rgba(114, 46, 209, 0.75)',
                        borderRadius: 6
                    }
                ]
            };

            const monthlyChartImage = localMonthlyVolumeData.length > 0
                ? await chartToBase64({
                    type: 'bar',
                    data: localMonthlyVolumeChartData,
                    options: {
                        responsive: false,
                        maintainAspectRatio: false,
                        plugins: { legend: { display: false } }
                    }
                }, 1000, 420)
                : '';

            const companyChartImage = localCompanyItems.length > 0
                ? await chartToBase64({
                    type: 'bar',
                    data: localCompanyChartData,
                    options: {
                        responsive: false,
                        maintainAspectRatio: false,
                        indexAxis: 'y',
                        plugins: { legend: { display: false } }
                    }
                }, 900, 420)
                : '';

            const topUsersChartImage = localTopUsersItems.length > 0
                ? await chartToBase64({
                    type: 'bar',
                    data: localTopUsersChartData,
                    options: {
                        responsive: false,
                        maintainAspectRatio: false,
                        indexAxis: 'y',
                        plugins: { legend: { display: false } }
                    }
                }, 900, 420)
                : '';

            const printWindow = window.open('', '_blank', 'width=1200,height=900');
            if (!printWindow) {
                throw new Error('Não foi possível abrir a janela de impressão. Verifique o bloqueador de pop-ups.');
            }

            const reportHtml = buildDashboardPrintHtml({
                monthlyChartImage,
                companyChartImage,
                topUsersChartImage
            });

            printWindow.document.write(reportHtml);
            printWindow.document.close();
            printWindow.onload = () => {
                window.setTimeout(() => {
                    printWindow.focus();
                    printWindow.print();
                }, 400);
            };

            message.success('Janela de exportação aberta. Escolha "Salvar como PDF" na impressão.');
        } catch (error) {
            message.error(error.message || 'Não foi possível exportar o dashboard em PDF.');
        } finally {
            setExportingPdf(false);
        }
    }, [buildDashboardPrintHtml, dashboardData, message]);

    const monthlyVolumeData = dashboardData?.monthlyVolume || [];
    const documentTypeItems = dashboardData?.documentTypes?.items || [];
    const topUsersItems = dashboardData?.topUsers?.items || [];
    const companyItems = dashboardData?.companiesUsed?.items || [];
    const hasDashboardUsageData = Number(dashboardData?.metrics?.totalPagesPrinted || 0) > 0;

    const monthlyVolumeChartData = useMemo(() => ({
        labels: monthlyVolumeData.map((item) => item.label),
        datasets: [
            {
                label: 'Páginas impressas',
                data: monthlyVolumeData.map((item) => item.totalPages),
                backgroundColor: 'rgba(24, 144, 255, 0.7)',
                borderRadius: 6
            }
        ]
    }), [monthlyVolumeData]);

    const companyChartData = useMemo(() => ({
        labels: companyItems.map((item) => item.companyName),
        datasets: [
            {
                label: 'Registros',
                data: companyItems.map((item) => item.totalPages),
                backgroundColor: 'rgba(82, 196, 26, 0.75)',
                borderRadius: 6
            }
        ]
    }), [companyItems]);

    const topUsersChartData = useMemo(() => ({
        labels: topUsersItems.map((item) => item.user),
        datasets: [
            {
                label: 'Páginas impressas',
                data: topUsersItems.map((item) => item.totalPages),
                backgroundColor: 'rgba(114, 46, 209, 0.75)',
                borderRadius: 6
            }
        ]
    }), [topUsersItems]);

    const topUsersColumns = [
        { title: 'Usuário', dataIndex: 'user', key: 'user' },
        { title: 'Páginas', dataIndex: 'totalPages', key: 'totalPages', width: 120 },
        { title: 'Reimpressões', dataIndex: 'totalReprints', key: 'totalReprints', width: 140 }
    ];

    const documentTypeColumns = [
        { title: 'Tipo de Documento', dataIndex: 'type', key: 'type' },
        { title: 'Páginas', dataIndex: 'totalPages', key: 'totalPages', width: 120 },
        { title: 'Reimpressões', dataIndex: 'totalReprints', key: 'totalReprints', width: 140 }
    ];

    const companyColumns = [
        { title: 'Filial / Unidade', dataIndex: 'companyName', key: 'companyName' },
        { title: 'Código', dataIndex: 'companyCode', key: 'companyCode', width: 120 },
        { title: 'Páginas', dataIndex: 'totalPages', key: 'totalPages', width: 120 },
        { title: 'Reimpressões', dataIndex: 'totalReprints', key: 'totalReprints', width: 140 }
    ];

    const displayPrinterName = currentImpressora?.nome || printerName || '';

    return (
        <Modal
            title={`Dashboard de Uso ${displayPrinterName ? ` - ${displayPrinterName}` : ''}`}
            open={visible}
            onCancel={onCancel}
            width="min(1100px, calc(100vw - 24px))"
            maskClosable={false}
            destroyOnHidden
            style={{ top: 5 }}
            footer={
                <Space>
                    <Button
                        icon={<DownloadOutlined />}
                        onClick={handleExportDashboardPdf}
                        loading={exportingPdf}
                        disabled={!dashboardData}
                    >
                        Exportar PDF
                    </Button>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => loadDashboard({ forceRefresh: true })}
                        loading={dashboardLoading}
                        disabled={!currentImpressora?.id}
                    >
                        Atualizar dados
                    </Button>
                    <Button onClick={onCancel}>
                        Fechar
                    </Button>
                </Space>
            }
            styles={{
                body: {
                    maxHeight: '70vh',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    padding: '16px 24px',
                    scrollbarWidth: 'thin'
                }
            }}
        >
            <Spin spinning={fetching}>
                {!currentImpressora && !fetching ? (
                    <Empty description="Selecione uma impressora válida para visualizar o dashboard de uso." />
                ) : (
                    <Space direction="vertical" size={16} style={{ width: '100%' }}>
                        <Row gutter={[12, 12]}>
                            <Col xs={24} md={8}>
                                <Text>Ano</Text>
                                <Select
                                    style={{ width: '100%', marginTop: 4 }}
                                    value={dashboardYear}
                                    onChange={setDashboardYear}
                                    options={dashboardYearOptions.map((year) => ({
                                        label: String(year),
                                        value: year
                                    }))}
                                />
                            </Col>
                            <Col xs={24} md={12}>
                                <Text>Período</Text>
                                <RangePicker
                                    style={{ width: '100%', marginTop: 4 }}
                                    value={dashboardPeriod}
                                    onChange={setDashboardPeriod}
                                    format="DD/MM/YYYY"
                                    allowClear
                                />
                            </Col>
                            <Col xs={24} md={4}>
                                <Text>&nbsp;</Text>
                                <Button
                                    block
                                    style={{ marginTop: 4 }}
                                    icon={<ReloadOutlined />}
                                    onClick={() => loadDashboard({ forceRefresh: true })}
                                    loading={dashboardLoading}
                                >
                                    Recarregar
                                </Button>
                            </Col>
                        </Row>

                        {(dashboardData?.printer || currentImpressora) && (
                            <Space wrap>
                                <Tag color="blue">{dashboardData?.printer?.nome || currentImpressora?.nome}</Tag>
                                <Tag>{dashboardData?.printer?.marca || currentImpressora?.marca_nome || currentImpressora?.marca || 'Marca não informada'}</Tag>
                                <Tag>{dashboardData?.printer?.modelo || currentImpressora?.modelo_nome || currentImpressora?.modelo || 'Modelo não informado'}</Tag>
                                <Tag color="purple">{dashboardData?.printer?.emulador || currentImpressora?.emulador || 'Emulador não informado'}</Tag>
                            </Space>
                        )}

                        {dashboardError && (
                            <Alert
                                type="error"
                                showIcon
                                message="Falha ao carregar o dashboard"
                                description={dashboardError}
                            />
                        )}

                        {dashboardLoading ? (
                            <div style={{ minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Spin size="large" />
                            </div>
                        ) : dashboardData ? (
                            <>
                                <Row gutter={[16, 16]}>
                                    <Col xs={24} sm={12} lg={6}>
                                        <Card>
                                            <Statistic
                                                title="Total de páginas impressas"
                                                value={dashboardData.metrics?.totalPagesPrinted || 0}
                                                prefix={<PrinterOutlined />}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={12} lg={6}>
                                        <Card>
                                            <Statistic
                                                title="Tipos de documentos"
                                                value={dashboardData.metrics?.totalDocuments || 0}
                                                prefix={<FileTextOutlined />}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={12} lg={6}>
                                        <Card>
                                            <Statistic
                                                title="Usuários ativos"
                                                value={dashboardData.metrics?.totalUsers || 0}
                                                prefix={<UserOutlined />}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={12} lg={6}>
                                        <Card>
                                            <Statistic
                                                title="Tempo de impressão"
                                                value={formatDashboardDuration(dashboardData.metrics?.estimatedTotalPrintTimeLabel)}
                                                prefix={<ClockCircleOutlined />}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={12} lg={6}>
                                        <Card>
                                            <Statistic
                                                title="Reimpressões"
                                                value={dashboardData.metrics?.totalReprints || 0}
                                                prefix={<ReloadOutlined />}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={12} lg={6}>
                                        <Card>
                                            <Statistic
                                                title="Filiais / Unidades utilizadas"
                                                value={dashboardData.metrics?.totalCompaniesUsed || 0}
                                                prefix={<BarChartOutlined />}
                                            />
                                        </Card>
                                    </Col>
                                </Row>

                                <Row gutter={[16, 16]}>
                                    <Col xs={24} lg={14}>
                                        <Card title="Volume de impressões por mês">
                                            {monthlyVolumeData.length > 0 ? (
                                                <div style={{ height: 320 }}>
                                                    <Bar
                                                        data={monthlyVolumeChartData}
                                                        options={{
                                                            responsive: true,
                                                            maintainAspectRatio: false,
                                                            plugins: {
                                                                legend: { display: false }
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                <Empty description="Nenhum volume mensal encontrado para os filtros selecionados." />
                                            )}
                                        </Card>
                                    </Col>
                                    <Col xs={24} lg={10}>
                                        <Card title="Filiais / Unidades e quantidade de registros">
                                            {companyItems.length > 0 ? (
                                                <div style={{ height: 320 }}>
                                                    <Bar
                                                        data={companyChartData}
                                                        options={{
                                                            responsive: true,
                                                            maintainAspectRatio: false,
                                                            indexAxis: 'y',
                                                            plugins: {
                                                                legend: { display: false }
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                <Empty description="Nenhuma filial/unidade encontrada para o período selecionado." />
                                            )}
                                        </Card>
                                    </Col>
                                </Row>

                                <Row gutter={[16, 16]}>
                                    <Col xs={24} lg={12}>
                                        <Card title="Usuários que mais utilizaram a impressora">
                                            {topUsersItems.length > 0 ? (
                                                <div style={{ height: 320 }}>
                                                    <Bar
                                                        data={topUsersChartData}
                                                        options={{
                                                            responsive: true,
                                                            maintainAspectRatio: false,
                                                            indexAxis: 'y',
                                                            plugins: {
                                                                legend: { display: false }
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                <Empty description="Nenhum usuário encontrado para o período selecionado." />
                                            )}
                                        </Card>
                                    </Col>
                                    <Col xs={24} lg={12}>
                                        <Card title="Resumo do período">
                                            {hasDashboardUsageData ? (
                                                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                                    <Text strong>Primeira impressão no período</Text>
                                                    <Text>{formatDashboardDate(dashboardData.metrics?.firstPrintAt)}</Text>
                                                    <Text strong>Última impressão no período</Text>
                                                    <Text>{formatDashboardDate(dashboardData.metrics?.lastPrintAt)}</Text>
                                                    <Text strong>Tempo total estimado</Text>
                                                    <Text>{formatDashboardDuration(dashboardData.metrics?.estimatedTotalPrintTimeLabel)}</Text>
                                                    <Text strong>Filtro aplicado</Text>
                                                    <Text>
                                                        {dashboardPeriod?.[0] && dashboardPeriod?.[1]
                                                            ? `${dashboardPeriod[0].format('DD/MM/YYYY')} até ${dashboardPeriod[1].format('DD/MM/YYYY')}`
                                                            : `Ano ${dashboardYear}`}
                                                    </Text>
                                                    <Text strong>Filiais / Unidades de uso</Text>
                                                    <Space wrap>
                                                        {companyItems.length > 0
                                                            ? companyItems.map((item) => (
                                                                <Tag key={`${item.companyCode}-${item.companyName}`}>
                                                                    {item.companyName}
                                                                </Tag>
                                                            ))
                                                            : <Text>Nenhuma filial/unidade encontrada.</Text>}
                                                    </Space>
                                                </Space>
                                            ) : (
                                                <Empty description="Sem estatísticas disponíveis para o período informado." />
                                            )}
                                        </Card>
                                    </Col>
                                </Row>

                                <Row gutter={[16, 16]}>
                                    <Col xs={24} lg={12}>
                                        <Card title="Ranking de usuários">
                                            <Table
                                                size="small"
                                                columns={topUsersColumns}
                                                dataSource={topUsersItems}
                                                rowKey="user"
                                                pagination={false}
                                                locale={{ emptyText: 'Nenhum usuário encontrado.' }}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} lg={12}>
                                        <Card title="Ranking por tipo de documento">
                                            <Table
                                                size="small"
                                                columns={documentTypeColumns}
                                                dataSource={documentTypeItems}
                                                rowKey="type"
                                                pagination={false}
                                                locale={{ emptyText: 'Nenhum tipo de documento encontrado.' }}
                                            />
                                        </Card>
                                    </Col>
                                </Row>

                                <Row gutter={[16, 16]}>
                                    <Col xs={24}>
                                        <Card title="Filiais / Unidades onde a impressora foi utilizada">
                                            <Table
                                                size="small"
                                                columns={companyColumns}
                                                dataSource={companyItems}
                                                rowKey={(record) => `${record.companyCode}-${record.companyName}`}
                                                pagination={false}
                                                locale={{ emptyText: 'Nenhuma filial/unidade encontrada.' }}
                                            />
                                        </Card>
                                    </Col>
                                </Row>
                            </>
                        ) : null}
                    </Space>
                )}
            </Spin>
        </Modal>
    );
};

export default ImpressoraDashboardModal;
